import { useState } from "react";
import { Plus, X, Globe } from "lucide-react";
import { cn } from "@/lib/utils";
import { useDeleteGrant, useGrants, useNodes } from "../../api/hooks.js";
import type { Grant, GrantKind } from "../../api/types.js";
import {
  Button,
  CenteredSpinner,
  EmptyState,
  ErrorState,
} from "../../components/ui.js";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../../components/ui/popover.js";
import { ConfirmDialog } from "../../components/ConfirmDialog.js";
import { AddGrantPopover } from "./AddGrantPopover.js";

// Chip rendering one grant. RECORD chips carry a distinct (sky) accent vs the
// driver/global colors used for PUBLISH.
function GrantChip({
  grant,
  nodeLabel,
  dimmed,
  onRemove,
}: {
  grant: Grant;
  nodeLabel: string | null;
  dimmed: boolean;
  onRemove: () => void;
}) {
  const isRecord = grant.kind === "record";
  let body: React.ReactNode;
  let tone: string;

  if (grant.scope === "global") {
    body = (
      <>
        <Globe className="h-3 w-3" />
        global
      </>
    );
    tone = "border-amber-500/40 bg-amber-500/15 text-amber-200";
  } else if (grant.scope === "driver") {
    const cf = grant.driver === "cloudflare";
    body = <>{grant.driver}</>;
    tone = cf
      ? "border-orange-500/30 bg-orange-500/10 text-orange-200"
      : "border-primary/30 bg-primary/10 text-primary";
  } else {
    body = <>{nodeLabel ?? grant.nodeId}</>;
    tone = "border-white/10 bg-white/5 text-foreground/90";
  }

  // RECORD overrides accent to sky.
  if (isRecord) {
    tone = "border-sky-500/40 bg-sky-500/10 text-sky-200";
  }

  return (
    <span
      className={cn(
        "inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs",
        tone,
        dimmed && "opacity-40",
      )}
      title={dimmed ? "Covered by a broader grant of the same kind" : undefined}
    >
      {body}
      <button
        onClick={onRemove}
        className="ml-0.5 rounded-full p-0.5 hover:bg-black/30"
        aria-label="Remove grant"
      >
        <X className="h-3 w-3" />
      </button>
    </span>
  );
}

function KindSection({
  title,
  grants,
  nodeLabelOf,
  globalPresent,
  driversCovered,
  onRemove,
}: {
  title: string;
  grants: Grant[];
  nodeLabelOf: (id: string | null) => string | null;
  globalPresent: boolean;
  driversCovered: Set<string>;
  onRemove: (g: Grant) => void;
}) {
  const global = grants.filter((g) => g.scope === "global");
  const drivers = grants.filter((g) => g.scope === "driver");
  const perNode = grants.filter((g) => g.scope === "node");

  function bucket(label: string, list: Grant[]) {
    if (list.length === 0) return null;
    return (
      <div className="mb-2">
        <div className="mb-1 font-mono text-[10px] uppercase tracking-[0.14em] text-muted-foreground">
          {label}
        </div>
        <div className="flex flex-wrap gap-1.5">
          {list.map((g) => {
            // Redundancy hint: dimmed if a broader grant of same kind covers it.
            const dimmed =
              g.scope === "node"
                ? globalPresent ||
                  (g.driver != null && driversCovered.has(g.driver)) ||
                  driversCovered.has(driverOfNodeChip(g, nodeLabelOf))
                : g.scope === "driver"
                  ? globalPresent
                  : false;
            return (
              <GrantChip
                key={g.id}
                grant={g}
                nodeLabel={nodeLabelOf(g.nodeId)}
                dimmed={dimmed}
                onRemove={() => onRemove(g)}
              />
            );
          })}
        </div>
      </div>
    );
  }

  return (
    <div className="mb-4">
      <h5 className="mb-1.5 font-mono text-[10px] font-semibold uppercase tracking-[0.16em] text-foreground/70">
        {title}
      </h5>
      {grants.length === 0 ? (
        <p className="text-xs text-muted-foreground">No grants</p>
      ) : (
        <>
          {bucket("Global", global)}
          {bucket("Driver-wide", drivers)}
          {bucket("Per-node", perNode)}
        </>
      )}
    </div>
  );
}

// A node chip's underlying driver isn't carried on a NODE grant directly; this
// returns "" so the driver-cover check is a no-op unless we can resolve it.
function driverOfNodeChip(
  _g: Grant,
  _nodeLabelOf: (id: string | null) => string | null,
): string {
  return "";
}

export function GrantsEditor({ userId }: { userId: string }) {
  const grants = useGrants(userId);
  const nodes = useNodes();
  const deleteGrant = useDeleteGrant(userId);
  const [showPopover, setShowPopover] = useState(false);
  const [pendingDelete, setPendingDelete] = useState<Grant | null>(null);

  function nodeLabelOf(id: string | null): string | null {
    if (!id) return null;
    const n = nodes.data?.find((x) => x.id === id);
    return n ? `${n.slug} · ${n.driver}` : id;
  }

  if (grants.isLoading) return <CenteredSpinner label="Loading grants…" />;
  if (grants.error) return <ErrorState message={grants.error.message} />;

  const all = grants.data ?? [];
  const byKind = (k: GrantKind) => all.filter((g) => g.kind === k);

  function coverage(k: GrantKind) {
    const list = byKind(k);
    return {
      globalPresent: list.some((g) => g.scope === "global"),
      driversCovered: new Set(
        list
          .filter((g) => g.scope === "driver" && g.driver)
          .map((g) => g.driver as string),
      ),
    };
  }

  const pubCov = coverage("publish");
  const recCov = coverage("record");

  async function confirmDelete() {
    if (!pendingDelete) return;
    try {
      await deleteGrant.mutateAsync(pendingDelete.id);
    } finally {
      setPendingDelete(null);
    }
  }

  return (
    <div>
      <div className="mb-3 flex items-center justify-between">
        <h4 className="font-display text-sm font-semibold text-foreground">Grants</h4>
        <Popover open={showPopover} onOpenChange={setShowPopover}>
          <PopoverTrigger asChild>
            <Button variant="secondary" size="sm">
              <Plus className="h-4 w-4" /> Grant
            </Button>
          </PopoverTrigger>
          <PopoverContent align="end" className="w-72">
            <AddGrantPopover
              userId={userId}
              onClose={() => setShowPopover(false)}
            />
          </PopoverContent>
        </Popover>
      </div>

      {all.length === 0 ? (
        <EmptyState
          title="No grants"
          hint="Add a publish or record grant to let this user stream."
        />
      ) : (
        <div className="rounded-xl border border-white/[0.06] bg-card/50 p-4">
          <KindSection
            title="Publishing"
            grants={byKind("publish")}
            nodeLabelOf={nodeLabelOf}
            globalPresent={pubCov.globalPresent}
            driversCovered={pubCov.driversCovered}
            onRemove={setPendingDelete}
          />
          <KindSection
            title="Recording"
            grants={byKind("record")}
            nodeLabelOf={nodeLabelOf}
            globalPresent={recCov.globalPresent}
            driversCovered={recCov.driversCovered}
            onRemove={setPendingDelete}
          />
        </div>
      )}

      <ConfirmDialog
        open={!!pendingDelete}
        title="Remove grant"
        destructive
        confirmLabel="Remove"
        loading={deleteGrant.isPending}
        body="This revokes the permission immediately. Continue?"
        onConfirm={confirmDelete}
        onCancel={() => setPendingDelete(null)}
      />
    </div>
  );
}
